import { useState, useEffect, useRef, ChangeEvent, FocusEvent } from 'react';
import {
	IconNavigationChevronUpComponent,
	IconNavigationChevronDownComponent,
	IconTravelReturnFlightComponent
} from '@vaa-component-lib/component.atom.icons';

import PopOverComponent, { PopOverType } from '../pop-over/pop-over.component';
import DropDownListComponent from '../drop-down-list/drop-down-list.component';
import DropDownListGroupComponent from '../drop-down-list/drop-down-list-group.component';

import sanitiseString from '../../lib/utils/sanitise-string';

import { SelectOption, SelectOptionGroup } from '../../types/config';
import { useFormContext } from 'react-hook-form';
import useMediaQuery, { BreakPointToken } from '../../hooks/useMediaQuery';
import CriteriaTriggerComponent from '../criteria-trigger/criteria-trigger.component';
import { TextFieldComponent } from '@vaa-component-lib/component.atom.text-field';

interface DynamicListSelectorComponentProps {
	label: string;
	name: string;
	id: string;
	ready?: boolean;
	options?: SelectOption[] | SelectOptionGroup[];
	onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
	onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
	hasGroups?: boolean;
	placeholder?: string;
	hasError?: boolean;
	feedback?: string;
	switchFocusTo?: () => void;
	iconChild?: React.ReactElement;
}

const DynamicListSelectorComponent = ({
	label,
	options,
	name,
	id,
	ready = false,
	onChange,
	onBlur,
	hasGroups = false,
	placeholder,
	switchFocusTo,
	iconChild,
	hasError,
	feedback
}: DynamicListSelectorComponentProps) => {
	const { setValue } = useFormContext();
	const [selected, setSelected] = useState<SelectOption>();
	const [isOpened, setIsOpened] = useState<boolean>(false);
	const [selectedLabel, setSelectedLabel] = useState<string>('');
	const ref = useRef<HTMLDivElement>(null);

	const isMobile = useMediaQuery(`(max-width: ${BreakPointToken.SmMax}px)`);
	const handleClick = () => setIsOpened(!isOpened);
	const handleClickOutside = (event: MouseEvent) => {
		if (ref.current && !ref.current.contains(event.target as Node)) {
			setIsOpened(false);
		}
	};

	const handleSelection = (
		option: SelectOption,
		userSelection: boolean
	): void => {
		const { selectedLabel, value } = option;

		if (selectedLabel === '') return;

		setSelectedLabel(selectedLabel);
		setIsOpened(false);
		setValue(name, value, {
			shouldValidate: true,
			shouldDirty: true
		});

		if (switchFocusTo && !isMobile && userSelection) {
			setTimeout(() => switchFocusTo(), 50);
		}
	};

	useEffect(() => {
		if (ready === true) {
			const selected = hasGroups
				? (options as SelectOptionGroup[])
						?.flatMap((group) => group?.options)
						.find((item) => item?.selected === true)
				: (options as SelectOption[])?.find((item) => item?.selected === true);

			if (selected) {
				setSelected(selected);
			}
		}
	}, [ready]);

	useEffect(() => {
		if (selected) {
			handleSelection(selected, false);
		}
	}, [selected]);

	useEffect(() => {
		if (document) {
			if (isOpened) {
				document.addEventListener('mousedown', handleClickOutside);
			}

			return () =>
				document.removeEventListener('mousedown', handleClickOutside);
		}
	}, [isOpened]);

	const popOverId = `popover-${sanitiseString(label)}`;

	return (
		<div ref={ref} data-cy="dynamic-list-selector-component">
			{isMobile && (
				<CriteriaTriggerComponent
					aria={{
						controls: '',
						expanded: false
					}}
					{...{ label }}
					value={selectedLabel || placeholder}
					onClick={(): void => handleClick()}
					iconChild={
						iconChild ? iconChild : <IconTravelReturnFlightComponent />
					}
					hasError={hasError}
					errorMessage={feedback}
				/>
			)}

			<div hidden={isMobile} onClick={(): void => handleClick()}>
				<TextFieldComponent
					label={label}
					id={id}
					value={selectedLabel || placeholder || label}
					autoComplete="off"
					spellCheck={false}
					placeholder={placeholder || label}
					aria-controls={popOverId}
					aria-haspopup="true"
					hasError={hasError}
					feedback={isOpened ? '' : feedback}
					iconChild={
						isOpened ? (
							<IconNavigationChevronUpComponent />
						) : (
							<IconNavigationChevronDownComponent />
						)
					}
					onIconClick={(): void => handleClick()}
					readonly
				/>
			</div>

			<PopOverComponent
				isVisible={isOpened}
				type={PopOverType.Selector}
				title={label}
				onClose={(): void => setIsOpened(false)}
				id={popOverId}
				isOverflow={hasGroups}
				scroll={hasGroups}>
				{hasGroups ? (
					<DropDownListGroupComponent
						items={options as SelectOptionGroup[]}
						onSelect={(option: SelectOption): void =>
							handleSelection(option, true)
						}
						isOpen={isOpened}
					/>
				) : (
					<DropDownListComponent
						items={options as SelectOption[]}
						onSelect={(option: SelectOption): void =>
							handleSelection(option, true)
						}
						isOpen={isOpened}
					/>
				)}
			</PopOverComponent>
		</div>
	);
};

export default DynamicListSelectorComponent;
